import React, { useState, useEffect } from "react";

// Components
import OnboardingProgress from "./components/OnboardingProgress";
import WelcomeStep from "./components/Steps/WelcomeStep";
import LegalStep from "./components/Steps/LegalStep";
import PracticeProviderStep from "./components/Steps/PracticeProviderStep";
import DefaultQuestionsStep from "./components/Steps/DefaultQuestionsStep";
import PaymentStep from "./components/Steps/PaymentStep";

// UI Components
import { Toaster } from "sonner";

// Logo asset served from public directory
import Logo from "../MVP/assets/Logo.png";

interface OnboardingData {
  practiceData?: any;
  questionsData?: any;
  paymentData?: any;
}

export default function NewOnboarding() {
  const [currentStep, setCurrentStep] = useState(0); // Start at welcome step
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    new Set([0]) // Welcome step is completed by default
  );
  const [canSkipLegal, setCanSkipLegal] = useState<boolean>(false);

  const steps = [
    "Welcome",
    "Legal",
    "Practice & Provider",
    "Default Questions",
    "Payment",
  ];

  useEffect(() => {
    // Check if user can skip legal step
    const checkCanSkipLegal = async () => {
      try {
        const response = await fetch('/dashboard/can-skip-legal', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setCanSkipLegal(data.can_skip_legal);
        }
      } catch (error) {
        console.error('Error checking skip legal status:', error);
      }
    };

    checkCanSkipLegal();
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      // Mark current step as completed when moving to next
      setCompletedSteps((prev) => new Set([...prev, currentStep]));

      let nextStep = currentStep + 1;

      // Skip legal step if user is allowed to and we're moving from welcome to legal
      if (canSkipLegal && currentStep === 0 && nextStep === 1) {
        nextStep = 2; // Skip to Practice & Provider step
        // Mark legal step as completed since we're skipping it
        setCompletedSteps((prev) => new Set([...prev, 1]));
      }

      setCurrentStep(nextStep);
    }
  };

  const handleStepData = (stepIndex: number, data: any) => {
    const newData = { ...onboardingData };

    switch (stepIndex) {
      case 2: // Practice & Provider
        newData.practiceData = data;
        break;
      case 3: // Default Questions
        newData.questionsData = data;
        break;
      case 4: // Payment
        newData.paymentData = data;
        break;
    }

    setOnboardingData(newData);
    // Mark current step as completed when data is saved
    setCompletedSteps((prev) => new Set([...prev, stepIndex]));

    // Handle next step progression, considering skipped legal step
    if (stepIndex < steps.length - 1) {
      let nextStep = stepIndex + 1;
      setCurrentStep(nextStep);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} />;

      case 1:
        // If user can skip legal step and we somehow end up here, show a skipped message
        if (canSkipLegal) {
          return (
            <div className="flex flex-col items-center justify-center p-8 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Step Skipped</h2>
                <p className="text-gray-600 mb-6">You have been authorized to skip the legal agreement step.</p>
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-[#005151] text-white rounded-lg hover:bg-[#004040] transition-colors"
                >
                  Continue to Practice & Provider
                </button>
              </div>
            </div>
          );
        }
        return <LegalStep onNext={handleNext} />;

      case 2:
        return (
          <PracticeProviderStep
            key={`practice-provider-${currentStep}`}
            onNext={(data) => handleStepData(2, data)}
            initialData={onboardingData.practiceData}
          />
        );

      case 3:
        return (
          <DefaultQuestionsStep
            onNext={(data) => handleStepData(3, data)}
            initialData={onboardingData.questionsData}
            practiceData={onboardingData.practiceData}
          />
        );

      case 4:
        return (
          <PaymentStep
            onNext={(data) => handleStepData(4, data)}
            initialData={onboardingData.paymentData}
            isFinalStep={true}
          />
        );

      default:
        return <WelcomeStep onNext={handleNext} />;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src={Logo}
                className="w-12 h-12 rounded-full cursor-pointer hover:opacity-70 transition-opacity"
                alt="Health Harbor"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                  Health Harbor
                </h1>
                <p className="text-xs text-gray-500 font-medium">
                  Account Setup
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </div>
              <div className="w-16 bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-[#005151] to-emerald-500 h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: `${((currentStep + 1) / steps.length) * 100}%`,
                  }}
                />
              </div>
            </div>
            {/* Mobile progress indicator */}
            <div className="sm:hidden text-xs text-gray-500 font-medium">
              {currentStep + 1}/{steps.length}
            </div>
          </div>
        </div>
      </div>
      <Toaster richColors position="top-center" />
      {/* Main Content - Scrollable Area */}
      <div className="flex-1 overflow-y-auto relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Progress Sidebar - Hidden on mobile, sticky on desktop */}
            <div className="hidden lg:block lg:col-span-3 xl:col-span-3">
              <div className="sticky top-8">
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-6">
                  <OnboardingProgress
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    canSkipLegal={canSkipLegal}
                  />
                </div>
              </div>
            </div>

            {/* Mobile Horizontal Stepper */}
            <div className="lg:hidden col-span-full mb-8">
              <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/30 p-4">
                <OnboardingProgress
                  currentStep={currentStep}
                  completedSteps={completedSteps}
                  canSkipLegal={canSkipLegal}
                  isMobile={true}
                />
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-9 xl:col-span-9">
              <div className="w-full">{renderCurrentStep()}</div>
            </div>
          </div>

          {/* Footer - Inside scrollable area */}
          <div className="border-t border-gray-200/50 mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="hidden sm:block">
                  © 2025 Health Harbor. All rights reserved.
                </div>
                <div className="flex items-center space-x-4">
                  <span className="hidden sm:inline">Need help?</span>
                  <a
                    href="mailto:support@healthharbor.co"
                    className="text-[#005151] hover:text-[#004040] font-medium transition-colors"
                  >
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
